import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { DollarSign, Building2, User, Mail, Lock, Clock } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    business_type: 'services',
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      const result = await register(formData);
      
      // Check if registration is pending approval
      if (result.pending) {
        setPendingApproval(true);
        toast.success('Account created! Awaiting approval.');
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const businessTypes = [
    { value: 'services', label: 'Services' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'ngo', label: 'Non-Profit / NGO' },
    { value: 'other', label: 'Other' }
  ];

  // Pending approval screen
  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[hsl(var(--surface-2))]">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock size={32} className="text-amber-600" />
            </div>
            <CardTitle className="text-2xl mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              Account Pending Approval
            </CardTitle>
            <CardDescription className="text-base mb-6">
              Your account has been created and is awaiting approval from our platform administrator. 
              You will receive an email notification once your account is approved.
            </CardDescription>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left Panel - Brand */}
      <div className="hidden lg:flex flex-col justify-center items-center p-12 bg-[linear-gradient(135deg,_hsl(189_70%_40%)_0%,_hsl(189_80%_30%)_100%)]">
        <div className="max-w-md text-center text-white">
          <div className="w-20 h-20 mx-auto mb-8 bg-white/20 rounded-2xl flex items-center justify-center">
            <DollarSign size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            TavJournals
          </h1>
          <p className="text-lg text-white/80">
            Get started in minutes. Track revenue, expenses, loans, and taxes with professional-grade accuracy.
          </p>
          
          <div className="mt-12 grid grid-cols-2 gap-6 text-left">
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-1">Double-Entry</h3>
              <p className="text-sm text-white/70">Professional accounting engine</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-1">Simple UI</h3>
              <p className="text-sm text-white/70">No accounting knowledge needed</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-1">Real-time</h3>
              <p className="text-sm text-white/70">Instant financial insights</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <h3 className="font-semibold mb-1">Secure</h3>
              <p className="text-sm text-white/70">Your data is protected</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex items-center justify-center p-6 bg-[hsl(var(--surface-2))]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="lg:hidden w-12 h-12 mx-auto mb-4 bg-[hsl(var(--brand-500))] rounded-xl flex items-center justify-center">
              <DollarSign size={24} className="text-white" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk' }}>
              Create your account
            </CardTitle>
            <CardDescription>
              Set up your company in minutes
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="company_name">
                  <Building2 size={14} className="inline mr-1" />
                  Company Name
                </Label>
                <Input
                  id="company_name"
                  placeholder="Acme Inc."
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="register-company-input"
                />
              </div>

              <div>
                <Label htmlFor="business_type">Business Type</Label>
                <Select
                  value={formData.business_type}
                  onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="register-business-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="name">
                  <User size={14} className="inline mr-1" />
                  Your Name
                </Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">
                  <Mail size={14} className="inline mr-1" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">
                  <Lock size={14} className="inline mr-1" />
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Min 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="mt-1"
                  data-testid="register-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-[hsl(var(--muted-ink))]">
              Already have an account?{' '}
              <Link to="/login" className="text-[hsl(var(--brand-600))] hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
